import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, ShieldCheck, Info, Check } from "lucide-react";
import DbsStatusBlock from "@/components/DbsStatusPill";
import DbsVerifyCta from "@/components/DbsVerifyCta";

const DECLARATIONS = [
  { key: "has_dbs", label: "I have a DBS certificate" },
  { key: "no_dbs", label: "I do not currently have a DBS certificate" },
  { key: "will_upload_later", label: "I will upload one later" },
];
const LEVELS = [
  { key: "basic", label: "Basic (default for domestic cleaning)" },
  { key: "standard", label: "Standard" },
  { key: "enhanced", label: "Enhanced" },
  { key: "unknown", label: "I'm not sure" },
];
const STATUS_LABEL = {
  not_provided: "Not provided",
  pending: "Pending verification",
  verified: "Verified",
  rejected: "Rejected",
  needs_resubmission: "Needs resubmission",
  previously_uploaded_not_verified: "Previously uploaded — not currently verified",
};

export default function DbsPanel() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ declaration: "", dbs_level: "basic", certificate_issue_date: "", certificate_number_last4: "", update_service_checked: false });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/dbs/mine");
    setData(data);
    setForm({
      declaration: data.declaration || "",
      dbs_level: data.dbs_level || "basic",
      certificate_issue_date: data.certificate_issue_date || "",
      certificate_number_last4: data.certificate_number_last4 || "",
      update_service_checked: !!data.update_service_checked,
    });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.declaration) return toast.error("Please choose one of the three options");
    setSaving(true);
    try {
      await api.post("/dbs/declare", {
        declaration: form.declaration,
        dbs_level: form.declaration === "has_dbs" ? form.dbs_level : null,
        certificate_issue_date: form.declaration === "has_dbs" ? (form.certificate_issue_date || null) : null,
        certificate_number_last4: form.certificate_number_last4 || null,
        update_service_checked: form.update_service_checked,
      });
      toast.success("DBS details saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const body = new FormData();
      body.append("file", file);
      await api.post("/dbs/document", body, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Certificate sent securely for verification");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-accent" />;
  const showUpdateService = ["standard", "enhanced"].includes(form.dbs_level);

  return (
    <div className="space-y-6 max-w-2xl" data-testid="dbs-panel">
      <div>
        <h3 className="font-display font-semibold">DBS verification</h3>
        <p className="text-sm text-muted-foreground mt-1">
          A DBS certificate is optional — you can work on My Clean Connect™ without one. Verified
          certificates earn the DBS Verified badge on your public profile.
        </p>
      </div>

      <DbsVerifyCta dbs={data} onChange={setData} variant="dashboard" />

      <div>
        <p className="text-xs text-muted-foreground mb-2">What customers currently see on your profile:</p>
        <DbsStatusBlock dbs={data.public} />
      </div>

      <div className="bg-card rounded-2xl border border-primary/5 p-5 space-y-2 text-sm" data-testid="dbs-record-summary">
        <Row k="My Clean Connect™ cleaner ID" v={data.mcc_ref || "—"} testid="dbs-mcc-ref-row" />
        <Row k="Verification status" v={data.simple?.label || STATUS_LABEL[data.verification_status] || data.verification_status} testid="dbs-verification-status" />
        <Row k="DBS level" v={data.dbs_level ? LEVELS.find((l) => l.key === data.dbs_level)?.label : "—"} />
        <Row k="Certificate issue date" v={data.certificate_issue_date || "—"} />
        <Row k="Verification provider" v={data.verification_provider || "—"} />
        <Row k="Provider reference" v={data.provider_reference || "—"} />
        <Row k="Verified on" v={data.verification_date || "—"} />
        <Row k="Fresh evidence due by" v={data.reverification_due_date || "—"} testid="dbs-due-date" />
        <Row k="Badge" v={data.badge_status === "active" ? "DBS Verified (active)" : data.badge_status === "suspended" ? "Suspended" : "Not earned"} />
        <Row k="Certificate on file" v={data.document_uploaded ? "Yes — visible only to our verification team" : "No"} />
      </div>

      <div className="bg-card rounded-2xl border border-primary/5 p-5 space-y-4" data-testid="dbs-declaration-form">
        <p className="text-sm font-semibold">Your DBS position</p>
        <div className="space-y-2">
          {DECLARATIONS.map((d) => (
            <button key={d.key} onClick={() => setForm({ ...form, declaration: d.key })}
              className={`w-full flex items-center gap-2.5 p-3 rounded-xl border-2 text-sm text-left transition-colors ${form.declaration === d.key ? "border-accent bg-accent/5" : "border-border hover:border-accent/40"}`}
              data-testid={`dbs-declaration-${d.key}`}>
              <span className={`h-5 w-5 rounded-full flex items-center justify-center border shrink-0 ${form.declaration === d.key ? "bg-accent border-accent text-white" : "border-border"}`}>
                {form.declaration === d.key && <Check className="h-3.5 w-3.5" />}
              </span>
              {d.label}
            </button>
          ))}
        </div>

        {form.declaration === "has_dbs" && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="dbs-details-fields">
            <label className="text-sm">DBS level
              <select value={form.dbs_level} onChange={(e) => setForm({ ...form, dbs_level: e.target.value })} className="dbs-input" data-testid="dbs-level-select">
                {LEVELS.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
              </select>
            </label>
            <label className="text-sm">Certificate issue date
              <input type="date" max={new Date().toISOString().split("T")[0]} value={form.certificate_issue_date}
                onChange={(e) => setForm({ ...form, certificate_issue_date: e.target.value })} className="dbs-input" data-testid="dbs-issue-date" />
            </label>
            <label className="text-sm">Last 4 digits of the certificate number
              <input value={form.certificate_number_last4} maxLength={4}
                onChange={(e) => setForm({ ...form, certificate_number_last4: e.target.value.replace(/\D/g, "") })}
                className="dbs-input" data-testid="dbs-last4" />
            </label>
            {showUpdateService && (
              <label className="text-sm flex items-start gap-2 sm:col-span-2">
                <input type="checkbox" checked={form.update_service_checked} onChange={(e) => setForm({ ...form, update_service_checked: e.target.checked })} className="mt-1" data-testid="dbs-update-service" />
                <span>My certificate is registered with the DBS Update Service (Standard and Enhanced only — the Update Service is not used for Basic certificates).</span>
              </label>
            )}
          </div>
        )}

        <button onClick={save} disabled={saving} className="px-6 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors inline-flex items-center gap-2 disabled:opacity-50" data-testid="dbs-save-btn">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save DBS details
        </button>
      </div>

      <div className="bg-card rounded-2xl border border-primary/5 p-5" data-testid="dbs-upload-block">
        <p className="text-sm font-semibold flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /> Provide your certificate for verification</p>
        <p className="text-sm text-muted-foreground mt-1">
          Optional. We don't order DBS checks — we only verify a certificate you already hold. Your
          certificate is stored securely for our verification team and is <strong>never shown to
          customers</strong> or published on your profile.
        </p>
        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" onChange={(e) => upload(e.target.files?.[0])} className="hidden" data-testid="dbs-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} className="mt-3 px-5 py-2.5 rounded-full border-2 border-accent text-accent font-medium inline-flex items-center gap-2 hover:bg-accent/5 transition-colors disabled:opacity-50" data-testid="dbs-upload-btn">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload certificate
        </button>
      </div>

        <div className="rounded-2xl border border-primary/5 bg-card p-4 text-sm" data-testid="dbs-badge-note">
          <p className="text-muted-foreground">
            Uploading a certificate does not award the DBS Verified badge on its own — while our team
            checks it, your profile shows "DBS submitted — verification pending". Full third-party
            DBS verification will be connected later.
          </p>
        </div>

      <div className="flex gap-3 p-4 rounded-xl bg-accent/10 text-sm" data-testid="dbs-freshness-note">
        <Info className="h-5 w-5 text-accent shrink-0" />
        <p>
          We'll remind you {data.reminder_days?.join(", ")} days before your evidence reaches the
          12-month My Clean Connect™ freshness limit, and again on the day it passes. {data.public?.freshness_note}
        </p>
      </div>
      <style>{`.dbs-input{width:100%;margin-top:0.25rem;padding:0.625rem 0.75rem;border-radius:0.75rem;border:1px solid hsl(var(--border));background:hsl(var(--background));outline:none}.dbs-input:focus{border-color:hsl(var(--accent))}`}</style>
    </div>
  );
}

function Row({ k, v, testid }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground">{k}</span>
      <span className="font-medium text-right" data-testid={testid}>{v}</span>
    </div>
  );
}
